import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type {
  CreateServiceLevelRequest,
  ServiceLevelMutationResponse,
  ServiceLevelRecord,
  ServiceLevelSummary,
  UpdateServiceLevelRequest,
} from '@nadoutong/contracts';
import { useSession } from '../hooks/useSession.tsx';
import {
  createServiceLevel,
  getOrgServiceLevel,
  listServiceLevelCatalogue,
  listServiceLevels,
  setOrgServiceLevel,
  updateServiceLevel,
  type OrgServiceLevelView,
} from './serviceLevels.ts';
import { TENANT_ROUTING_KEY } from './useTenantRouting.ts';

export const SERVICE_LEVELS_KEY = ['service-levels'] as const;

/**
 * The admin rows and the customer projection come off one endpoint but are
 * NOT the same value, so they must not share a cache entry — a projected row
 * parked under the record key would type as carrying `routing_config_id` while
 * carrying nothing.
 */
const SERVICE_LEVEL_RECORDS_KEY = [...SERVICE_LEVELS_KEY, 'records'] as const;
const SERVICE_LEVEL_CATALOGUE_KEY = [...SERVICE_LEVELS_KEY, 'catalogue'] as const;

export function orgServiceLevelKey(
  orgId: string,
): readonly ['service-levels', 'orgs', string] {
  return [...SERVICE_LEVELS_KEY, 'orgs', orgId] as const;
}

/**
 * A level write moves member orgs' routing assignments, so every mutation here
 * also invalidates the tenant-routing root the admin routing page reads.
 */
function invalidateServiceLevelQueries(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: SERVICE_LEVELS_KEY });
  void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
}

/** Admin-only: full rows including the anchored routing config. */
export function useServiceLevels(): UseQueryResult<readonly ServiceLevelRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: SERVICE_LEVEL_RECORDS_KEY,
    queryFn: async ({ signal }) => {
      const response = await listServiceLevels(fetchImpl, signal);
      return response.levels;
    },
  });
}

/** Customer surface: names and descriptions, no routing identity. */
export function useServiceLevelCatalogue(): UseQueryResult<readonly ServiceLevelSummary[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: SERVICE_LEVEL_CATALOGUE_KEY,
    queryFn: async ({ signal }) => {
      const response = await listServiceLevelCatalogue(fetchImpl, signal);
      return response.levels;
    },
  });
}

export function useOrgServiceLevel(orgId: string): UseQueryResult<OrgServiceLevelView> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: orgServiceLevelKey(orgId),
    queryFn: ({ signal }) => getOrgServiceLevel(orgId, fetchImpl, signal),
    enabled: orgId.length > 0,
  });
}

export function useCreateServiceLevel(): UseMutationResult<
  ServiceLevelMutationResponse,
  Error,
  CreateServiceLevelRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateServiceLevelRequest) => createServiceLevel(body, fetchImpl),
    onSuccess: () => { invalidateServiceLevelQueries(qc); },
  });
}

export function useUpdateServiceLevel(): UseMutationResult<
  ServiceLevelMutationResponse,
  Error,
  { levelId: string; body: UpdateServiceLevelRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ levelId, body }) => updateServiceLevel(levelId, body, fetchImpl),
    onSuccess: () => { invalidateServiceLevelQueries(qc); },
  });
}

export function useSetOrgServiceLevel(): UseMutationResult<
  OrgServiceLevelView,
  Error,
  { orgId: string; levelId: string | null }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, levelId }) => setOrgServiceLevel(orgId, { level_id: levelId }, fetchImpl),
    onSuccess: (view, { orgId }) => {
      // The response IS the new assignment, so seed it rather than showing the
      // pre-write value until the invalidated read comes back.
      qc.setQueryData(orgServiceLevelKey(orgId), view);
      invalidateServiceLevelQueries(qc);
    },
  });
}
